/**
 * Route metadata read by the app's single document-title owner.
 *
 * react-router types both `RouteObject.handle` and `UIMatch.handle` as
 * `unknown`, so nothing checks a route's handle against this shape where it is
 * declared. `satisfies RouteHandle` at the declaration site and the narrowing
 * predicate at the read site are what keep the two ends honest without a cast.
 */

import type { UIMatch } from 'react-router-dom'

export interface RouteHandle {
  /** Tab title for this route. Routes that declare none inherit the default. */
  title?: string
}

/** A handle that carries a title, as narrowed by {@link hasRouteTitle}. */
export type TitledRouteHandle = RouteHandle & { title: string }

/**
 * The title for routes that declare none.
 *
 * Must stay identical to the static `<title>` in index.html: that node is what
 * the tab reads until React's first commit, so on an untitled route any other
 * value here would visibly rewrite the tab a moment after first paint.
 */
export const DEFAULT_DOCUMENT_TITLE = 'Alveary'

export function hasRouteTitle(handle: unknown): handle is TitledRouteHandle {
  return (
    typeof handle === 'object' &&
    handle !== null &&
    'title' in handle &&
    typeof handle.title === 'string'
  )
}

/** Deepest declared title wins, so a child route can override its parent's. */
export function resolveDocumentTitle(matches: readonly UIMatch[]): string {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const { handle } = matches[index]
    if (hasRouteTitle(handle)) {
      return handle.title
    }
  }

  return DEFAULT_DOCUMENT_TITLE
}
